/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-anonymous-default-export */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Input, Button, Modal, Icon, FormPro } from '@roo/roo'
import PopConfirm from '@roo/roo/PopConfirm'
import TreeSelect from '@roo/roo0119/TreeSelect'
import ConfigProvider from '@roo/roo0119/ConfigProvider'
import { useTranslation } from '@sailor/i18n-web'
import { useRequest } from 'ahooks'
import {
  fetchDepartmentTree,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  IDepartmentTreeNode,
  IDeptConflictItem,
} from '@/pages/staff/common/departmentApi'
import { failToast, successToast } from '@/common/utils/toast'
import CommonTable from '@/components/CommonTable'
import TipText from '@/components/TipText'
import QuestionTooltip from '@/components/QuestionTooltip'
import styles from './index.module.less'
import { useDeptTreeSelect } from '@/hooks'

type ModalType = 'create' | 'createSub' | 'edit'

interface DeptModalState {
  visible: boolean;
  type: ModalType;
  record?: IDepartmentTreeNode;
  /** createSub 时的父部门 */
  parentDept?: IDepartmentTreeNode;
}

type FlatNode = IDepartmentTreeNode & { level: number; hasChildren: boolean }

/** 将树形数据展开为带有 level 信息的扁平数组，用于 Table 展示
 *  注意：展开后去掉 children 字段，避免 TableNew 把每行当成树形数据再渲染一次展开图标
 */
const flattenTree = (nodes: IDepartmentTreeNode[], level = 0): FlatNode[] => {
  const result: FlatNode[] = []
  for (const node of nodes) {
    const hasChildren = Boolean(node.children && node.children.length > 0)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children: _children, ...rest } = node
    result.push({ ...rest, level, hasChildren })
    if (hasChildren) {
      result.push(...flattenTree(node.children!, level + 1))
    }
  }
  return result
}

/**
 * 判断一棵子树中是否存在任意 matched === true 的节点
 */
const hasMatchedInSubtree = (node: IDepartmentTreeNode): boolean => {
  if (node.matched) return true
  if (node.children && node.children.length > 0) {
    return node.children.some(hasMatchedInSubtree)
  }
  return false
}

/**
 * 遍历树，收集应该收起的节点 id：
 * - 有子节点 且 自身及所有后代均无 matched===true → 收起
 * - 有子节点 且 子树中存在 matched===true → 展开（展示命中路径）
 */
const collectCollapsedByMatched = (nodes: IDepartmentTreeNode[]): Set<number> => {
  const collapsed = new Set<number>()
  const traverse = (list: IDepartmentTreeNode[]) => {
    for (const node of list) {
      if (node.children && node.children.length > 0) {
        if (!hasMatchedInSubtree(node)) {
          collapsed.add(node.id)
        }
        traverse(node.children)
      }
    }
  }
  traverse(nodes)
  return collapsed
}

/** 根据折叠 id 集合过滤出可见行（折叠节点的所有后代均隐藏） */
const filterByCollapsed = (allNodes: FlatNode[], collapsedIds: Set<number>): FlatNode[] => {
  const hiddenAncestorIds = new Set<number>()
  return allNodes.filter(node => {
    // 若父节点在折叠集合中，则隐藏当前节点，并把自己也加入隐藏集合（传递给更深的后代）
    if (collapsedIds.has(node.parentId) || hiddenAncestorIds.has(node.parentId)) {
      hiddenAncestorIds.add(node.id)
      return false
    }
    return true
  })
}

export default () => {
  const { t, i18n } = useTranslation()
  const [keyword, setKeyword] = useState('')
  const [inputVal, setInputVal] = useState('')
  const [deptModal, setDeptModal] = useState<DeptModalState>({ visible: false, type: 'create' })
  // useRequest 的 async 回调会捕获闭包，用 ref 保存最新值，避免读到旧 state
  const deptModalRef = useRef(deptModal)
  useEffect(() => {deptModalRef.current = deptModal}, [deptModal])
  const [deletingDept, setDeletingDept] = useState<IDepartmentTreeNode | null>(null)
  const [form] = FormPro.useForm()
  // 折叠的节点 id 集合（默认全部收起，数据加载后初始化）
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set())
  const [collapsedInitialized, setCollapsedInitialized] = useState(false)

  const handleToggleCollapse = useCallback((id: number) => {
    setCollapsedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  // 新增弹窗行数据（脱离 FormPro.List，用原生 state 管理）
  type CreateRow = { key: string; name: string; parentId: number | null; nameError?: string; parentError?: string; }
  const [createRows, setCreateRows] = useState<CreateRow[]>([{ key: `row-${Date.now()}`, name: '', parentId: null }])
  // useRequest async 闭包无法感知 state 更新，用 ref 同步最新值
  const createRowsRef = useRef(createRows)
  useEffect(() => {createRowsRef.current = createRows}, [createRows])

  const handleRowNameChange = useCallback((index: number, value: string) => {
    setCreateRows(prev => prev.map((row, i) => i === index ? { ...row, name: value, nameError: undefined } : row))
  }, [])

  // 同级重名扫描：对 rows 做全量扫描，返回更新后的 rows
  // - parentId !== null 且名称在同一 parentId 下重复 → 标记 error
  // - parentId 为 null 或名称唯一 → 清除重名 error
  const DUPLICATE_ERR = t('e_pc_duplicate_dept_exist', '存在重复部门，请调整')
  const scanDuplicates = useCallback(<T extends { name: string; parentId: number | null; nameError?: string }>(rows: T[]): T[] => {
    const countMap: Record<string, number> = {}
    rows.forEach(row => {
      if (row.parentId === null || !row.name.trim()) return
      const key = `${row.parentId}__${row.name.trim()}`
      countMap[key] = (countMap[key] || 0) + 1
    })
    return rows.map(row => {
      if (row.parentId === null) {
        return row.nameError === DUPLICATE_ERR ? { ...row, nameError: undefined } : row
      }
      const key = `${row.parentId}__${row.name.trim()}`
      if (row.name.trim() && countMap[key] > 1) {
        return { ...row, nameError: DUPLICATE_ERR }
      }
      return row.nameError === DUPLICATE_ERR ? { ...row, nameError: undefined } : row
    })
  }, [DUPLICATE_ERR])

  // 部门名称失焦时触发全量重名扫描
  const handleRowNameBlur = useCallback(() => {
    setCreateRows(prev => scanDuplicates(prev))
  }, [scanDuplicates])

  // 上级部门变更时也触发全量重名扫描（用户先填名称再选上级部门时的场景）
  const handleRowParentChange = useCallback((index: number, value: number | null) => {
    setCreateRows(prev => {
      const updated = prev.map((row, i) => i === index ? { ...row, parentId: value, parentError: undefined } : row)
      return scanDuplicates(updated)
    })
  }, [scanDuplicates])

  const handleRowAdd = useCallback(() => {
    // createSub 模式下新增行需继承父部门 ID，否则 scanDuplicates 会因 parentId===null 跳过重名检查
    const { type, parentDept } = deptModalRef.current
    const inheritedParentId = type === 'createSub' ? (parentDept?.id ?? null) : null
    setCreateRows(prev => [...prev, { key: `row-${Date.now()}`, name: '', parentId: inheritedParentId }])
  }, [])

  const handleRowRemove = useCallback((index: number) => {
    setCreateRows(prev => {
      const filtered = prev.filter((_, i) => i !== index)
      return scanDuplicates(filtered)
    })
  }, [scanDuplicates])

  // 列表查询：标准分页器
  const [pageNo, setPageNo] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const { data: listRes, loading, run: refreshTree } = useRequest(
    () => fetchDepartmentTree({ keyword: keyword || undefined, pageNo, pageSize, searchMode: 'TREE' }),
    { refreshDeps: [keyword, pageNo, pageSize] }
  )

  const treeData: IDepartmentTreeNode[] = Array.isArray(listRes?.data?.result) ? listRes!.data!.result : []
  const totalCount = listRes?.data?.totalCount ?? 0

  const handlePageChange = (_pageNo: number, _pageSize: number) => {
    _pageNo !== pageNo && setPageNo(_pageNo)
    _pageSize !== pageSize && setPageSize(_pageSize)
  }

  // 上级部门 TreeSelect：懒加载（弹窗打开时 init），清空时 reset
  const {
    treeOptions: parentTreeOptions,
    isSearching: isParentSearching,
    init: initParentTree,
    reset: resetParentTree,
    handleSearch: handleParentSearch,
    handlePopupScroll: handleParentPopupScroll,
  } = useDeptTreeSelect(t('e_pc_no_parent_dept', '无上级部门（一级部门）'))

  const allFlatData = useMemo(() => flattenTree(treeData), [treeData])


  // 根据 matched 字段初始化折叠状态：
  // - 有搜索词：遗历树，matched===true 的节点展开，其余收起
  // - 无搜索词：有子节点的全部默认收起
  useEffect(() => {
    if (treeData.length === 0) {
      if (collapsedInitialized) setCollapsedIds(new Set())
      return
    }
    if (keyword) {
      // 搜索模式：根据接口返回的 matched 字段决定展开/收起
      setCollapsedIds(collectCollapsedByMatched(treeData))
    } else {
      // 无搜索词：默认全部展开
      setCollapsedIds(new Set())
      setCollapsedInitialized(true)
    }
  }, [treeData, keyword])

  const flatData = useMemo(() => filterByCollapsed(allFlatData, collapsedIds), [allFlatData, collapsedIds])

  const handleSearch = () => {
    setPageNo(1)
    setKeyword(inputVal)
  }

  const handleOpenCreate = () => {
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, name: '', parentId: null }])
    setDeptModal({ visible: true, type: 'create' })
    initParentTree()
  }

  const handleOpenCreateSub = (record: IDepartmentTreeNode) => {
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, name: '', parentId: record.id }])
    setDeptModal({ visible: true, type: 'createSub', parentDept: record })
  }

  const handleOpenEdit = (record: IDepartmentTreeNode) => {
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, name: record.name, parentId: null }])
    setDeptModal({ visible: true, type: 'edit', record })
    initParentTree()
  }

  const checkBeforeDelete = (record: FlatNode): boolean => {
    if (record.hasChildren) {
      failToast(t('e_pc_delete_dept_has_sub_error', '存在子部门，无法删除'))
      return false
    }
    if (record.staffCount > 0) {
      failToast(t('e_pc_delete_dept_has_staff_error', '此部门包含员工，无法删除'))
      return false
    }
    return true
  }

  const handleModalClose = () => {
    setDeptModal(prev => ({ ...prev, visible: false }))
    form.resetFields()
    setCreateRows([{ key: `row-${Date.now()}`, name: '', parentId: null }])
  }

  const { run: handleModalSubmit, loading: submitting } = useRequest(
    async () => {
      // 通过 ref 读取最新 state，避免 useRequest 闭包捕获旧值
      const { type, record } = deptModalRef.current
      const latestRows = createRowsRef.current
      if (type === 'edit' && record) {
        // 编辑模式：从 createRows[0] 取值校验
        const row = latestRows[0]
        if (!row.name.trim()) {
          setCreateRows(prev => prev.map((r, i) => i === 0 ? { ...r, nameError: t('e_pc_input_dept_name', '请输入部门名称') } : r))
          return
        }
        const updateRes = await updateDepartment({ departmentId: record.id, name: row.name.trim() })
        if (!updateRes.success) return
        successToast(t('e_pc_update_success', '修改成功'))
        handleModalClose()
        refreshTree()
      } else {
        // 批量新增（create / createSub）：校验
        let hasError = false
        const validated = latestRows.map(row => {
          const nextRow = { ...row }
          if (!row.name.trim()) {
            hasError = true
            nextRow.nameError = t('e_pc_input_dept_name', '请输入部门名称')
          }
          // createSub 时 parentId 已由父组件注入，不需要校验
          if (type !== 'createSub' && row.parentId === null) {
            hasError = true
            nextRow.parentError = t('e_pc_select_superior_dept', '请选择上级部门')
          }
          return nextRow
        })
        if (hasError) {
          setCreateRows(validated)
          return
        }
        // 兜底校验同级重名
        const duplicateValidated = scanDuplicates(validated)
        if (duplicateValidated.some(row => row.nameError === DUPLICATE_ERR)) {
          setCreateRows(duplicateValidated)
          return
        }
        const batchRes = await createDepartment({
          departmentCreateList: latestRows.map(row => ({
            name: row.name.trim(),
            parentId: row.parentId ?? 0,
          })),
        })
        if (!batchRes.success) return
        // 接口统一返回 code=0，通过 conflictList 区分：空=创建成功，非空=存在重名冲突
        // 仅用 name 匹配（同一批次请求的 parentId 相同，不需要再联合匹配 parentId）
        const conflictList: IDeptConflictItem[] = batchRes.data?.conflictList || []
        if (conflictList.length > 0) {
          const conflictNames = new Set(conflictList.map(item => item.name))
          setCreateRows(prev => prev.map(row => {
            const isConflict = conflictNames.has(row.name.trim())
            return isConflict
              ? { ...row, nameError: t('e_pc_dept_exist', '该部门名称已存在，请更换后重试') }
              : row
          }))
          return
        }
        successToast(t('e_pc_create_success', '创建成功'))
        handleModalClose()
        refreshTree()
      }
    },
    { manual: true }
  )

  const { run: handleConfirmDelete, loading: deleting } = useRequest(
    async (dept: IDepartmentTreeNode) => {
      const res = await deleteDepartment({ departmentId: dept.id })
      if (res.success) {
        successToast(t('e_pc_del_success', '删除成功'))
        setDeletingDept(null)
        refreshTree()
      }
    },
    { manual: true }
  )

  const modalTitle = () => {
    const { type, parentDept } = deptModal
    if (type === 'edit') return t('e_pc_edit_dept', '编辑部门')
    if (type === 'createSub') return t('e_pc_add_sub_dept_to', '新增子部门到{dept}', { dept: parentDept?.name || '' })
    return t('e_pc_add_dept', '新增部门')
  }

  const columns = [
    {
      dataIndex: 'name',
      title: t('e_pc_dept_name_label', '部门'),
      render: (name: string, record: FlatNode) => (
        <div className={styles.tableNameCell} style={{ paddingLeft: `${record.level * 24}px` }}>
          {record.hasChildren ? (
            <span
              className={styles.collapseBtn}
              onClick={() => handleToggleCollapse(record.id)}
              aria-label={collapsedIds.has(record.id) ? '展开' : '收起'}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: collapsedIds.has(record.id) ? 'rotate(-90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  display: 'block',
                }}
              >
                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          ) : (
            <span className={styles.collapsePlaceholder} />
          )}
          <TipText text={name} />
        </div>
      ),
    },
    {
      dataIndex: 'staffCount',
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {t('e_pc_staff_count', '员工数量')}
          <QuestionTooltip style={{}}>{t('e_pc_excel_group_number_explanation', '包含本部门直属员工及所有下级部门的员工总数。员工数量 = 本部门直属员工 + 所有子部门员工（含子部门的子部门，逐级累加）')}</QuestionTooltip>
        </span>
      ),
      width: 160,
    },
    {
      dataIndex: '',
      title: t('batch_common_operation', '操作'),
      // eslint-disable-next-line @sailor/i18n-no-country-codes
      width: i18n.language === 'en' ? 320 : 240,
      fixed: 'right',
      render: (_: never, record: FlatNode) => (
        <div>
          <Button type="brand-text" onClick={() => handleOpenCreateSub(record)}>
            {t('e_pc_add_sub_dept', '新增子部门')}
          </Button>&ensp;&ensp;
          <Button type="brand-text" onClick={() => handleOpenEdit(record)}>
            {t('e_pc_edit', '编辑')}
          </Button>&ensp;&ensp;
          <PopConfirm
            content={t('e_pc_delete_dept_confirm', '确认删除此部门？')}
            placement="left"
            onConfirm={() => handleConfirmDelete(record)}
            cancelText={t('e_pc_cancel', '取消')}
            confirmText={t('e_pc_submit', '确认')}
            disabled={deleting || record.hasChildren || record.staffCount > 0}
          >
            <Button
              type="brand-text"
              disabled={deleting}
              onClick={() => {
                checkBeforeDelete(record)
              }}
            >
              {t('e_pc_del', '删除')}
            </Button>
          </PopConfirm>
        </div>
      ),
    },
  ]

  const isEdit = deptModal.type === 'edit'
  const isCreateSub = deptModal.type === 'createSub'

  const renderModalContent = () => {
    if (isEdit) {
      const row = createRows[0]
      return (
        // 编辑部门：单行单列，复用自定义 label 风格
        <div className={styles.modalFormList}>
          <div className={styles.formListItem}>
            <div className={styles.formListFields}>
              <div className={styles.formListColLabel}>
                <span className={styles.formListColRequired}>*&nbsp;</span>
                {t('e_pc_dept_name_label', '部门名称')}
              </div>
              <Input
                value={row?.name || ''}
                status={row?.nameError ? 'error' : undefined}
                placeholder={t('e_pc_input_dept_name', '请输入部门名称')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRowNameChange(0, e.target.value)}
                style={{ width: '100%' }}
              />
              {row?.nameError && <div className={styles.formListColError}>{row.nameError}</div>}
            </div>
          </div>
        </div>
      )
    }

    if (isCreateSub) {
      return (
        /* 新增子部门：只有部门名称，无上级部门选择器（父部门已固定在标题中） */
        <div>
          <div className={styles.modalFormList}>
            {createRows.map((row, index) => (
              <div key={row.key} className={styles.formListItem}>
                <span className={styles.formListIndex}>{index + 1}</span>
                <div className={`${styles.formListFields} ${styles.formListFieldsSingleCol}`}>
                  <div className={styles.formListColLabel}>
                    <span className={styles.formListColRequired}>*&nbsp;</span>
                    {t('e_pc_dept_name_label', '部门名称')}
                  </div>
                  <Input
                    value={row.name}
                    status={row.nameError ? 'error' : undefined}
                    placeholder={t('e_pc_input_dept_name', '请输入部门名称')}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRowNameChange(index, e.target.value)}
                    onBlur={handleRowNameBlur}
                    style={{ width: '100%' }}
                  />
                  {row.nameError && <div className={styles.formListColError}>{row.nameError}</div>}
                </div>
                {createRows.length > 1 && (
                  <Button type="brand-text" onClick={() => handleRowRemove(index)} className={styles.formListDeleteBtn}>
                    <Icon name="trash-o" size={20} />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <div className={styles.addRowBtnWrap}>
            <Button className={styles.addRowBtn} type="brand-text" onClick={handleRowAdd}>
              + {t('e_pc_add_another', '继续添加一条')}
            </Button>
          </div>
        </div>
      )
    }

    // 默认：新增部门，双列布局（部门名称 + 上级部门）
    return (
      <div>
        <div className={styles.modalFormList}>
          {createRows.map((row, index) => (
            <div key={row.key} className={styles.formListItem}>
              <span className={styles.formListIndex}>{index + 1}</span>
              <div className={styles.formListFields}>
                <div className={styles.formListFieldsRow}>
                  <div className={styles.formListCol}>
                    <div className={styles.formListColLabel}>
                      <span className={styles.formListColRequired}>*&nbsp;</span>
                      {t('e_pc_dept_name_label', '部门名称')}
                    </div>
                    <Input
                      value={row.name}
                      status={row.nameError ? 'error' : undefined}
                      placeholder={t('e_pc_input_dept_name', '请输入部门名称')}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRowNameChange(index, e.target.value)}
                      onBlur={handleRowNameBlur}
                      style={{ width: '100%' }}
                    />
                    {row.nameError && <div className={styles.formListColError}>{row.nameError}</div>}
                  </div>
                  <div className={styles.formListCol}>
                    <div className={styles.formListColLabel}>
                      <span className={styles.formListColRequired}>*&nbsp;</span>
                      {t('e_pc_parent_dept', '上级部门')}
                    </div>
                    <div className={styles.deptTreeSelectWrap}>
                      <ConfigProvider config={{ prefixCls: 'roo0119' }}>
                        <TreeSelect
                          useNew
                          variant="filled"
                          status={row.parentError ? 'error' : undefined}
                          value={row.parentId ?? undefined}
                          treeData={parentTreeOptions}
                          treeDefaultExpandAll={isParentSearching}
                          placeholder={t('e_pc_select_superior_dept', '请选择上级部门')}
                          showSearch={{ onSearch: handleParentSearch }}
                          onChange={(val) => {
                            handleRowParentChange(index, (val as number) ?? null)
                            // 清空选择时重置数据到首页
                            if (val === null || val === undefined) resetParentTree()
                          }}
                          onPopupScroll={handleParentPopupScroll}
                          allowClear
                        />
                      </ConfigProvider>
                    </div>
                    {row.parentError && <div className={styles.formListColError}>{row.parentError}</div>}
                  </div>
                </div>
              </div>
              {createRows.length > 1 && (
                <Button type="brand-text" onClick={() => handleRowRemove(index)} className={styles.formListDeleteBtn}>
                  <Icon name="trash-o" size={20} />
                </Button>
              )}
            </div>
          ))}
        </div>
        <div className={styles.addRowBtnWrap}>
          <Button className={styles.addRowBtn} type="brand-text" onClick={handleRowAdd}>
              + {t('e_pc_add_another', '继续添加一条')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <FormPro layout="vertical" size="small" labelCol={{ span: 12 }} wrapperCol={{ span: 12 }} style={{ paddingBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 12px', alignItems: 'end' }}>
          <FormPro.Item label={t('e_pc_dept_name_label', '部门名称')} style={{ marginBottom: 0 }}>
            <Input
              allowClear
              placeholder={t('e_pc_input_dept_name', '请输入部门名称')}
              value={inputVal}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputVal(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') handleSearch()
              }}
            />
          </FormPro.Item>
          <div style={{ gridColumn: 'span 3', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <Button type="brand" size="normal" htmlType="button" onClick={handleSearch} className="btn-dark">
              {t('e_pc_aquire', '查询')}
            </Button>
          </div>
        </div>
      </FormPro>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="brand" size="normal" htmlType="button" onClick={handleOpenCreate}>
          {t('e_pc_add_dept', '新增部门')}
        </Button>
      </div>

      <CommonTable
        rowKey="id"
        columns={columns}
        data={loading ? [] : flatData}
        loading={loading}
        pagination={{
          total: totalCount,
          currentPage: pageNo,
          pageSize,
          onChange: handlePageChange,
        }}
      />

      {/* 新增/编辑部门弹窗 */}
      <Modal
        title={modalTitle()}
        visible={deptModal.visible}
        onCancel={handleModalClose}
        showCancelButton={false}
        showConfirmButton={false}
        closeMotion={false}
        backdropClosable={false}
        style={{ width: isEdit || isCreateSub ? '480px' : '680px' }}
      >
        <FormPro
          form={form}
          colon={false}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 18 }}
          onFinish={handleModalSubmit}
        >
          {renderModalContent()}
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '20px', gap: '12px' }}>
            <Button type="hollow" onClick={handleModalClose}>
              {t('e_pc_cancel_button', '取消')}
            </Button>
            <Button type="brand" onClick={() => handleModalSubmit()} loading={submitting}>
              {t('e_pc_confirm_button', '确认')}
            </Button>
          </div>
        </FormPro>
      </Modal>

    </div>
  )
}
